import { Component, ChangeDetectorRef, InjectionToken, Injector, OnDestroy, OnInit, Type } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PsaStepCoBenefitsComponent } from "src/app/components/steps-project-register/step-coBenefits/step-coBenefits.component";
import { PsaStepConfirmationComponent } from "src/app/components/steps-project-register/step-confirmation/step-confirmation.component";
import { PsaStepGeneralDataComponent } from "src/app/components/steps-project-register/step-general-data/step-general-data.component";
import { PsaStepMethodologyComponent } from "src/app/components/steps-project-register/step-methodology/step-methodology.component";
import { PsaStepPropertyComponent } from "src/app/components/steps-project-register/step-property/step-property.component";
import { ProjectRegisterFeedback, ProjectRegisterFeedbackModalAction } from "./project-register-feedback.model";
import { ActivatedRoute, Router } from '@angular/router';
import { PayloadSanitizerUtil } from "src/app/helpers/utils/payload-sanitizer.util";
import { extractApiError } from "src/app/helpers/utils/api-error.util";
import { buildPsaSelectItemStub } from "src/app/helpers/utils/psa-baseline-methodology.util";
import { ValidateInstitutionalRegisterAccount } from "src/app/helpers/utils/account.util";
import { Developer, Locations, PsaProjectData, PsaProjectRegisterFormRegister, PsaProjectResponse } from "src/app/shared/interfaces/psa-project-register";
import { catchError, firstValueFrom, map, Observable, Subscription } from "rxjs";
import { ProjectService } from "../../services/project.service";
import { formatCPFOrCNPJ, removeNonDigitsCNPJ, removeNonDigitsCPF } from "src/app/helpers/utils/cpf-cnpj.util";
import { CpfCnpjValidator } from "src/app/helpers/Validators/CpfCnpjValidator/cpf-cnpj.validator";
import { PROJECT_REGISTER_ENFORCE_STEP_VALIDATION, PROJECT_REGISTER_STEP_FIELDS, PROJECT_REGISTER_URL_PATTERN } from "./project-register-step.fields";
import { AppToastService } from "src/app/shared/services/app-toast.service";

export const FORM_REGISTER = new InjectionToken<FormGroup>('formRegister');

export interface WizardStepNavigation {
  goToStep(index: number): void;
}

export const WIZARD_STEP_NAV = new InjectionToken<WizardStepNavigation>('wizardStepNav');

@Component({
  standalone: false,
  selector: 'app-project-register',
  templateUrl: './project-register.component.html',
  styleUrls: ['./project-register.component.scss']
})
export class ProjectRegisterComponent implements OnInit, OnDestroy {
  formRegister: FormGroup = new FormGroup({});
  currentStep = 0;
  canAdvance = false;
  readonly enforceStepValidation = PROJECT_REGISTER_ENFORCE_STEP_VALIDATION;
  private formChangesSub?: Subscription;
  idProject: number = 0;
  projectDraftData: PsaProjectData | undefined;
  isSavingDraft = false;
  isFinalizing = false;
  isSubmitConfirmOpen = false;
  isFeedbackModalOpen = false;
  stepContentVersion = 0;
  pageFeedback: ProjectRegisterFeedback | null = null;

  steps: { title: string; description: string; content: Type<any>; injector: Injector }[] = [];

  readonly wizardStepsDisplay = [
    { title: 'Dados Gerais', description: 'Detalhes do projeto e desenvolvedores' },
    { title: 'Propriedade', description: 'Informações sobre a área e localização' },
    { title: 'Metodologia', description: 'Modelos e padrões aplicados' },
    { title: 'Co-benefícios', description: 'Benefícios sociais e ecológicos' },
    { title: 'Confirmação', description: 'Revisão e envio' },
  ];

  get stepProgress(): string {
    return `${this.currentStep + 1}/${this.wizardStepsDisplay.length}`;
  }

  get currentStepTitle(): string {
    const step = this.steps[this.currentStep];
    return step ? `${this.currentStep + 1} - ${step.title}` : '';
  }

  get isConfirmationStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  // // quando tiver a api de cidades remover este item
  // selectCity: B3SelectItem[] = [
  //   { inputId: 1, label: "São Paulo", data: 1 }
  // ];

  constructor(
    private fb: FormBuilder,
    private injector: Injector,
    private router: Router,
    private routeActivate: ActivatedRoute,
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef,
    private readonly toastService: AppToastService,
  ) { }

  ngOnInit() {
    this.initFormRegister();

    this.routeActivate.paramMap.subscribe(params => {
      const paramId = params.get('id');
      if (paramId) {
        this.idProject = Number(paramId);
        this.loadFormRegister();
      }
    });

    this.routeActivate.queryParamMap.subscribe(params => {
      const stepParam = params.get('step');
      if (stepParam == null) {
        return;
      }

      const stepIndex = Number(stepParam);
      if (Number.isNaN(stepIndex) || stepIndex < 0 || stepIndex >= this.steps.length) {
        return;
      }

      this.currentStep = stepIndex;
      this.updateCanAdvance();
    });

    this.formChangesSub = this.formRegister.valueChanges.subscribe(() => {
      this.updateCanAdvance();
    });
    this.updateCanAdvance();

    this.updateSteps();
  }

  ngOnDestroy(): void {
    this.formChangesSub?.unsubscribe();
  }

  updateSteps() {
    this.steps = [
      {
        title: 'Dados Gerais',
        description: 'Detalhes do projeto e desenvolvedores',
        content: PsaStepGeneralDataComponent,
        injector: this.createInjector(),
      },
      {
        title: 'Propriedade',
        description: 'Informações sobre a área e localização',
        content: PsaStepPropertyComponent,
        injector: this.createInjector(),
      },
      {
        title: 'Metodologia',
        description: 'Modelos e padrões aplicados',
        content: PsaStepMethodologyComponent,
        injector: this.createInjector(),
      },
      {
        title: 'Co-benefícios',
        description: 'Benefícios sociais e ecológicos',
        content: PsaStepCoBenefitsComponent,
        injector: this.createInjector(),
      },
      {
        title: 'Confirmação',
        description: 'Revisão e envio',
        content: PsaStepConfirmationComponent,
        injector: this.createInjector(),
      },
    ];
  }


  initFormRegister(): void {
    this.formRegister = this.fb.group({
      projectName: ['', [Validators.required, Validators.maxLength(100)]],
      registerCode: ['', [Validators.required, ValidateInstitutionalRegisterAccount()]],
      projectStartDate: ['', [Validators.required]],
      baselineActivity: ['', [Validators.required]],
      generalDetails: ['', [Validators.required, Validators.maxLength(500)]],
      developerCompanyName: ['', [Validators.required, Validators.maxLength(100)]],
      developerCnpj: ['', [Validators.required, Validators.maxLength(18), CpfCnpjValidator.validate]],
      developerEmail: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      developerPhone: ['', [Validators.required, Validators.maxLength(15)]],
      developerCompanyNameAdd: ['', [Validators.maxLength(100)]],
      developerCnpjAdd: ['', [Validators.maxLength(18), CpfCnpjValidator.validate]],
      developers: [[]],

      landOwnerFullName: ['', [Validators.maxLength(100)]],
      landOwnerCpf: ['', [Validators.maxLength(18), CpfCnpjValidator.validate]],
      lat: ['', [Validators.maxLength(50)]],
      long: ['', [Validators.maxLength(50)]],
      country: [''],
      uf: [''],
      city: [''],
      totalHectares: ['', [Validators.maxLength(10)]],
      carbonGenerationHectares: ['', [Validators.maxLength(10)]],
      availableHectares: [''],
      biome: [''],
      registrationRecord: ['', [Validators.maxLength(150)]],
      onlineStatementLink: ['', [Validators.maxLength(150)]],
      locations: [[], [Validators.required]],

      methodology: ['', [Validators.required]],
      ramComplianceReportLink: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(PROJECT_REGISTER_URL_PATTERN),
      ]],
      ramComplianceReportDetail: ['', [Validators.required, Validators.maxLength(200)]],
      documentDescription: ['', [Validators.maxLength(500)]],

      relevantDocumentationAdd: ['', [Validators.maxLength(100)]],
      attachments: [[]],

      odsNumbers: [[], [Validators.required]],
      coBenefits: ['', [Validators.required, Validators.maxLength(500)]],
    });
  }

  loadFormRegister(): void {
    this.loadProjectDraft().subscribe(projectDraft => {
      this.projectDraftData = projectDraft;

      const developerPrincipal: Developer | undefined = projectDraft?.developers?.find(d => d.principal);

      this.formRegister.patchValue({
        projectName: projectDraft?.projectName || "",
        registerCode: projectDraft?.registerCode || "",
        developerCompanyName: developerPrincipal ? developerPrincipal.developerName : "",
        developerCnpj: developerPrincipal ? formatCPFOrCNPJ(developerPrincipal.cpfCnpj || '') : "",
        developerEmail: developerPrincipal ? developerPrincipal.email : "",
        developerPhone: developerPrincipal ? developerPrincipal.phone : "",
        generalDetails: projectDraft?.generalDetails || "",
        developers: projectDraft?.developers || [],
        baselineActivity: projectDraft?.baselineActivityId != null
          ? buildPsaSelectItemStub(projectDraft.baselineActivityId)
          : '',
        methodology: projectDraft?.methodologyId != null
          ? buildPsaSelectItemStub(projectDraft.methodologyId)
          : '',

        locations: projectDraft?.locations || [],

        ramComplianceReportLink: projectDraft?.methodologyReportLink || "",
        ramComplianceReportDetail: projectDraft?.methodologyDocumentDescription || "",
        documentDescription: projectDraft?.otherDocumentationDescription || "",

        attachments: projectDraft?.attachments || [],

        odsNumbers: projectDraft?.odsNumbers || [1],
        coBenefits: projectDraft?.coBenefits || "",
      });

      this.updateSteps();
      this.stepContentVersion++;
      this.updateCanAdvance();
    });
  }

  createInjector(): Injector {
    return Injector.create({
      providers: [
        { provide: FORM_REGISTER, useValue: this.formRegister },
        { provide: 'projectDraftData', useValue: this.projectDraftData },
        { provide: WIZARD_STEP_NAV, useValue: { goToStep: (index: number) => this.goToStep(index) } },
      ],
      parent: this.injector
    });
  }

  onStepSelected(index: number): void {
    if (index >= 0 && index <= this.currentStep) {
      this.currentStep = index;
      this.updateCanAdvance();
    }
  }

  goToStep(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      this.currentStep = index;
      this.stepContentVersion++;
      this.updateCanAdvance();
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.updateCanAdvance();
    }
  }

  async nextStep() {
    if (this.enforceStepValidation && !this.canAdvance) {
      this.markCurrentStepFieldsTouched();
      return;
    }

    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
      this.updateCanAdvance();
    }
    await this.saveOrUpdateProjectDraft();
  }

  /** Etapas 1–3: nextStep; etapa 4: salva e vai para confirmação. */
  async onAdvanceClick(): Promise<void> {
    if (this.currentStep === this.steps.length - 2) {
      await this.advanceToConfirmation();
      return;
    }

    await this.nextStep();
  }

  updateCanAdvance(): void {
    this.canAdvance =
      !PROJECT_REGISTER_ENFORCE_STEP_VALIDATION || this.isCurrentStepValid();
  }

  isCurrentStepValid(): boolean {
    const fields = PROJECT_REGISTER_STEP_FIELDS[this.currentStep] ?? [];

    return fields.every(fieldName => {
      const control = this.formRegister.get(fieldName);
      if (!control || control.disabled) {
        return true;
      }
      return control.valid;
    });
  }

  private markCurrentStepFieldsTouched(): void {
    const fields = PROJECT_REGISTER_STEP_FIELDS[this.currentStep] ?? [];
    fields.forEach(fieldName => this.formRegister.get(fieldName)?.markAsTouched());
  }

  async saveOrUpdateProjectDraft() {
    try {
      const formRegisterValue: PsaProjectRegisterFormRegister = this.formRegister.value;
      this.addDeveloperPrincipal(formRegisterValue);

      if (this.idProject > 0) {
       // update draft
       const res: PsaProjectResponse = await firstValueFrom(this.projectService.projectDraftUpdate(formRegisterValue, this.idProject));
       this.syncDraftFromResponse(res);
      } else {
        // save draft
        const res: PsaProjectResponse = await firstValueFrom(this.projectService.projectDraftRegister(formRegisterValue));
        this.idProject = res.data?.projectId || 0;
        this.syncDraftFromResponse(res);
      }
    } catch (erro) {
      console.error("exception: ", erro);
    }
  }

  private syncDevelopersFromResponse(res: PsaProjectResponse): void {
    this.syncDraftFromResponse(res);
  }

  private syncDraftFromResponse(res: PsaProjectResponse): void {
    if (!res.data) {
      return;
    }

    if (res.data.developers) {
      this.formRegister.patchValue({ developers: res.data.developers }, { emitEvent: false });
    }

    if (this.projectDraftData) {
      this.projectDraftData.baselineActivityId = res.data.baselineActivityId ?? this.projectDraftData.baselineActivityId;
      this.projectDraftData.methodologyId = res.data.methodologyId ?? this.projectDraftData.methodologyId;
    }
  }

  addDeveloperPrincipal(formRegisterValue: PsaProjectRegisterFormRegister) {
    const existingPrincipal = formRegisterValue?.developers?.find(d => d.principal);
    const developerPrincipal: Developer = {
      ...(existingPrincipal?.id != null ? { id: existingPrincipal.id } : {}),
      cpfCnpj: removeNonDigitsCNPJ(formRegisterValue.developerCnpj || "") || "",
      developerName: formRegisterValue.developerCompanyName || "",
      phone: formRegisterValue.developerPhone || "",
      email: formRegisterValue.developerEmail || "",
      principal: true
    };

    if (developerPrincipal.cpfCnpj && developerPrincipal.developerName) {
      formRegisterValue.developers = [
        ...(formRegisterValue.developers?.filter(d => !d.principal) ?? []),
        developerPrincipal,
      ];
    }
  }

  loadProjectDraft(): Observable<PsaProjectData | undefined> {
    return this.projectService.getProjectDraftById(Number(this.idProject)).pipe(
      map((res: PsaProjectResponse) => res?.data || undefined),
      catchError(error => {
        console.error("Erro ao carregar rascunho do projeto. Stack error: ", error);
        this.router.navigate(['/registro-projeto']);
        return [undefined];
      })
    );
  }

  async advanceToConfirmation(): Promise<void> {
    if (this.enforceStepValidation && !this.isCurrentStepValid()) {
      this.markCurrentStepFieldsTouched();
      return;
    }

    await this.saveDraftBeforeConfirmation();
    this.currentStep = this.steps.length - 1;
    this.stepContentVersion++;
    this.updateCanAdvance();
  }

  saveDraft(): void {
    if (this.isSavingDraft) {
      return;
    }

    this.isSavingDraft = true;
    const formRegisterValue = this.buildSanitizedForm();
    const save$ = this.idProject > 0
      ? this.projectService.projectDraftUpdate(formRegisterValue, this.idProject)
      : this.projectService.projectDraftRegister(formRegisterValue);

    save$.subscribe({
      next: (res: PsaProjectResponse) => {
        if (this.idProject === 0) {
          this.idProject = res.data?.projectId || 0;
        }
        this.syncDraftFromResponse(res);
        this.isSavingDraft = false;
        this.showDraftSavedToast();
      },
      error: err => {
        console.error('[SAVE_DRAFT][error]', err);
        this.isSavingDraft = false;
      },
    });
  }

  finalizeProject(): void {
    if (this.isFinalizing || this.idProject <= 0) {
      return;
    }

    this.isSubmitConfirmOpen = true;
  }

  onSubmitConfirmModalClosed(confirmed: boolean): void {
    this.isSubmitConfirmOpen = false;
    this.releaseStuckDialogBackdrops();

    if (!confirmed) {
      return;
    }

    window.setTimeout(() => {
      void this.submitProjectAfterConfirmation();
    }, 150);
  }

  onFeedbackModalClosed(action: ProjectRegisterFeedbackModalAction): void {
    const feedback = this.pageFeedback;
    this.isFeedbackModalOpen = false;
    this.pageFeedback = null;
    this.releaseStuckDialogBackdrops();
    this.cdr.markForCheck();

    if (action === 'primary' && feedback?.variant === 'success') {
      this.router.navigate(['/consulta-projeto']);
    }
  }

  private async submitProjectAfterConfirmation(): Promise<void> {
    if (this.isFinalizing || this.idProject <= 0) {
      return;
    }

    this.pageFeedback = null;
    this.isFeedbackModalOpen = false;
    this.isFinalizing = true;
    this.cdr.markForCheck();

    try {
      const sanitized = this.buildSanitizedForm();
      const updateRes: PsaProjectResponse = await firstValueFrom(
        this.projectService.projectDraftUpdate(sanitized, this.idProject),
      );
      this.syncDevelopersFromResponse(updateRes);
      const res: PsaProjectResponse = await firstValueFrom(
        this.projectService.projectDraftRegisterFinalize(sanitized, this.idProject),
      );
      this.handleFinalizeSuccess(res);
    } catch (error) {
      console.error('Erro ao finalizar o rascunho - stackError: ', error);
      if (!this.enforceStepValidation) {
        this.handleFinalizeSuccess({} as PsaProjectResponse);
      } else {
        this.handleFinalizeError(error);
      }
    } finally {
      this.isFinalizing = false;
      this.cdr.markForCheck();
    }
  }

  private handleFinalizeSuccess(_res: PsaProjectResponse): void {
    this.toastService.show({
      variant: 'success',
      label: 'Projeto enviado para validação!',
      description: 'Acompanhe o processo na aba Projeto de Crédito > Consulta',
    });
    this.router.navigate(['/consulta-projeto']);
  }

  private handleFinalizeError(erro: unknown): void {
    const apiError = extractApiError(erro);

    this.openFeedbackModal({
      variant: 'error',
      heading: 'O envio das informações falhou.',
      description: apiError.message || apiError.details || 'Por favor, tente novamente.',
      primaryActionLabel: 'Ok, entendi',
    });
  }

  private showDraftSavedToast(): void {
    this.toastService.show({
      variant: 'warning',
      label: 'Projeto Salvo como Rascunho!',
      description: this.getProjectDisplayName(),
    });
  }

  private openFeedbackModal(feedback: ProjectRegisterFeedback): void {
    this.releaseStuckDialogBackdrops();
    this.pageFeedback = feedback;
    this.isFeedbackModalOpen = true;
    this.cdr.markForCheck();
  }

  private releaseStuckDialogBackdrops(): void {
    document.querySelectorAll('dialog[open]').forEach(element => {
      if (element instanceof HTMLDialogElement) {
        element.close();
      }
    });
  }

  private getProjectDisplayName(): string {
    const name = this.formRegister.get('projectName')?.value;
    if (typeof name === 'string' && name.trim()) {
      return name.trim();
    }

    return this.projectDraftData?.projectName?.trim() || 'Projeto de crédito';
  }

  private buildSanitizedForm(): PsaProjectRegisterFormRegister {
    const raw = this.formRegister.value;
    const sanitized = PayloadSanitizerUtil.sanitize(raw, true);
    this.addDeveloperPrincipal(sanitized);
    return sanitized;
  }

  private async saveDraftBeforeConfirmation(): Promise<void> {
    const saveTask = this.saveOrUpdateProjectDraft();

    if (!this.enforceStepValidation) {
      await Promise.race([
        saveTask,
        new Promise<void>(resolve => setTimeout(resolve, 2000)),
      ]);
      return;
    }

    await saveTask;
  }
}
